import React, {useCallback, useEffect, useRef, useState} from "react";
import {useAuth} from "../auth";
import {apiFetch, apiFetchBlob, ApiError} from "../api/client";
import {isErrorCode, messageForCode} from "../api/error-codes";
import type {ErrorCode} from "../generated/openapi-contract";
import {API_BASE} from "../api/config";
import "./MerchantActions.css";

interface Cancellation {status: string; reason: string; createdAt: string; evidenceId: string | null}
interface CancelContext {allowed: boolean; requiresEvidence: boolean; contextToken: string; blockedReason?: string; cancellation?: Cancellation}
interface Evidence {id: string; reason: string; note: string; contentType: string; createdAt: string; contextToken: string}
interface Command {requestId: string; contextToken: string; reason: string; evidenceId: string | null}
const reasons: Record<string, string> = {bank_declined: "Банк отказал в переводе", transfer_blocked: "Перевод заблокирован", payment_error: "Ошибка оплаты", other: "Другая причина", merchant_cancelled: "Отменена мерчантом"};
const blockers: Partial<Record<ErrorCode, string>> = {
  order_disputed: "По заявке открыт спор. Отмена выполняется после разбора.",
  order_has_paid_parts: "По заявке уже есть успешная оплата. Самостоятельная отмена недоступна.",
  payment_already_reported: "Уже приложено подтверждение оплаты. Для отмены обратитесь к поддержке.",
  cancellation_review_required: "История оплаты требует проверки. Обратитесь к поддержке.",
  cancellation_context_changed: "Реквизиты или состояние заявки изменились. Обновите данные перед отменой.",
  failure_evidence_required: "Приложите доказательство неудачной оплаты для текущих реквизитов.",
  order_not_cancellable: "В текущем состоянии отмена недоступна.",
  order_cancelled: "Заявка уже отменена.",
  idempotency_conflict: "Этот запрос уже принят с другими данными. Обновите состояние заявки.",
};
function message(error: unknown): string {
  return messageForCode(error, blockers) ?? "Не удалось подтвердить результат. Повторите тот же запрос или обновите состояние.";
}
function uncertain(error: unknown): boolean {return !(error instanceof ApiError) || error.status >= 500 || error.status === 0;}

export function FailureEvidenceList({orderId, operator = false, refreshKey = "", hideEmpty = !operator}: {orderId: string; operator?: boolean; refreshKey?: string; hideEmpty?: boolean}): JSX.Element | null {
  const {token} = useAuth();
  const [items, setItems] = useState<Evidence[]>([]);
  const [error, setError] = useState("");
  const base = `${operator ? "/cancellation" : "/merchant/me"}/orders/${encodeURIComponent(orderId)}/failure-evidence`;
  useEffect(() => {
    let alive = true;
    const load = () => { void apiFetch<{items: Evidence[]}>(base, token).then((value) => {if (alive) {setItems(value.items); setError("");}}).catch(() => {if (alive) setError("Не удалось загрузить доказательства отказа.");}); };
    load(); const timer = window.setInterval(load, 30_000);
    return () => {alive = false; window.clearInterval(timer);};
  }, [base, token, refreshKey]);
  const download = async (item: Evidence): Promise<void> => {
    try {
      const file = await apiFetchBlob(`${base}/${encodeURIComponent(item.id)}`, token);
      const url = URL.createObjectURL(file); const a = document.createElement("a");
      const ext = item.contentType === "application/pdf" ? "pdf" : item.contentType.split("/")[1];
      a.href = url; a.download = `payment-failure.${ext}`; a.click(); window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {setError("Не удалось скачать доказательство отказа.");}
  };
  // Пустой блок мерчанту и трейдеру ничего не сообщает; в разборе спора
  // админ видит и «пока нет».
  if (hideEmpty && items.length === 0 && !error) return null;
  return <section className="merchant-section-card" aria-label="Доказательства неудачной оплаты">
    <h3>Доказательства неудачной оплаты</h3>
    {error && <p role="alert">{error}</p>}
    {items.length === 0 ? <p>Файлов отказа банка пока нет.</p> : <ul>{items.map((item) => <li key={item.id}>
      <strong>{reasons[item.reason] ?? item.reason}</strong> · {new Date(item.createdAt).toLocaleString()}
      {item.note && <p>{item.note}</p>}
      <button className="btn btn-ghost" type="button" onClick={() => void download(item)}>Скачать доказательство</button>
    </li>)}</ul>}
  </section>;
}

export function MerchantCancellation({orderId, refreshKey, onCancelled}: {orderId: string; refreshKey: string; onCancelled: () => void}): JSX.Element {
  const {token} = useAuth();
  const [context, setContext] = useState<CancelContext | null>(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("bank_declined");
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(false);
  const [evidenceVersion, setEvidenceVersion] = useState(0);
  const pending = useRef<{uploadId: string; command: Command; note: string; file: File | null; uploadUncertain: boolean; cancelUncertain: boolean} | null>(null);
  const base = `/merchant/me/orders/${encodeURIComponent(orderId)}`;
  const refresh = useCallback(async (): Promise<void> => {
    try {setContext(await apiFetch<CancelContext>(`${base}/cancellation-context`, token));}
    catch {setError("Не удалось проверить доступность отмены.");}
  }, [base, token]);
  useEffect(() => {void refresh(); const timer = window.setInterval(() => void refresh(), 30_000); return () => window.clearInterval(timer);}, [refresh, refreshKey]);
  const submit = async (): Promise<void> => {
    if (busy || !context) return;
    if (!pending.current) {
      if (!context.allowed) return;
      if (context.requiresEvidence && (!file || (reason === "other" && !note.trim()))) {setError("Приложите отказ банка и укажите причину."); return;}
      pending.current = {uploadId: crypto.randomUUID(), command: {requestId: crypto.randomUUID(), contextToken: context.contextToken, reason: context.requiresEvidence ? reason : "merchant_cancelled", evidenceId: null}, note, file: context.requiresEvidence ? file : null, uploadUncertain: false, cancelUncertain: false};
    }
    const operation = pending.current;
    setBusy(true); setError("");
    try {
      if (operation.file && !operation.command.evidenceId) {
        const body = new FormData(); body.set("requestId", operation.uploadId); body.set("contextToken", operation.command.contextToken);
        body.set("reason", operation.command.reason); body.set("note", operation.note); body.set("file", operation.file);
        let response: Response;
        try {response = await fetch(`${API_BASE}${base}/failure-evidence`, {method: "POST", headers: {Authorization: `Bearer ${token}`}, body});}
        catch (cause) {operation.uploadUncertain = true; throw cause;}
        if (!response.ok) {
          if (response.status >= 500) operation.uploadUncertain = true;
          throw new ApiError(response.status, "Upload failed", await response.text());
        }
        let accepted: Evidence;
        try {
          accepted = await response.json() as Evidence;
          if (!accepted?.id) throw new Error("Missing evidence ID");
        } catch (cause) {operation.uploadUncertain = true; throw cause;}
        operation.command.evidenceId = accepted.id; operation.uploadUncertain = false; setEvidenceVersion((value) => value + 1);
      }
      try {await apiFetch<Cancellation>(`${base}/cancel`, token, {method: "POST", body: JSON.stringify(operation.command)});}
      catch (cause) {if (uncertain(cause)) operation.cancelUncertain = true; throw cause;}
      pending.current = null; setRetry(false); setOpen(false); setFile(null); await refresh(); onCancelled();
    } catch (cause) {
      // An earlier unknown result remains unknown after a later conflict.
      const keep = operation.uploadUncertain || operation.cancelUncertain || uncertain(cause);
      setRetry(keep); if (!keep) pending.current = null;
      setError(message(cause)); await refresh();
    } finally {setBusy(false);}
  };
  // Отмена в принципе невозможна (закрытая заявка, не тот тип) — блок не
  // показывается; причины, которые мерчанту стоит знать (спор, оплаченная
  // часть, чек уже приложен), остаются.
  const unavailable = !context || (!context.allowed && !context.cancellation && (!context.blockedReason || context.blockedReason === "order_not_cancellable"));
  const hidden = unavailable && !open && !retry && !error;
  return <>
    {!hidden && <section className="merchant-section-card merchant-cancellation" aria-label="Отмена заявки мерчантом">
      <h3>{context?.cancellation ? "Отменена мерчантом" : "Отмена заявки"}</h3>
      {context?.cancellation && <p>{reasons[context.cancellation.reason]} · {new Date(context.cancellation.createdAt).toLocaleString()}</p>}
      {context?.blockedReason && !context.cancellation && <p>{(isErrorCode(context.blockedReason) ? blockers[context.blockedReason] : undefined) ?? "Отмена недоступна."}</p>}
      {error && <p role="alert">{error}</p>}
      {context?.allowed && !open && <button type="button" className="btn btn-ghost" onClick={() => setOpen(true)}>{context.requiresEvidence ? "Не удалось оплатить" : "Отменить заявку"}</button>}
      {(open || retry) && <div className="merchant-cancellation-form">
        {context?.requiresEvidence && !retry && <>
          <p>Приложите отказ или ошибку банка. Если деньги списались или перевод ещё обрабатывается, обратитесь к поддержке.</p>
          <label>Причина<select value={reason} onChange={(event) => setReason(event.target.value)} disabled={busy}>{Object.entries(reasons).filter(([key]) => key !== "merchant_cancelled").map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
          <label>Пояснение<textarea maxLength={1000} value={note} onChange={(event) => setNote(event.target.value)} disabled={busy}/></label>
          <label>Доказательство неудачной оплаты<input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(event) => setFile(event.target.files?.[0] ?? null)} disabled={busy}/></label>
          <p>PDF, JPG, PNG или WebP до 15 MiB{file ? ` · ${file.name}` : ""}</p>
        </>}
        <p>Заявка будет закрыта и останется в истории.</p>
        <button className="btn btn-primary" type="button" disabled={busy} onClick={() => void submit()}>{busy ? "Отмена…" : retry ? "Проверить отмену" : "Подтвердить отмену"}</button>
        {!retry && <button className="btn btn-ghost" type="button" disabled={busy} onClick={() => setOpen(false)}>Продолжить заявку</button>}
      </div>}
      {(retry || error) && <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void refresh()}>Обновить состояние отмены</button>}
    </section>}
    <FailureEvidenceList orderId={orderId} refreshKey={`${refreshKey}:${evidenceVersion}`}/>
  </>;
}
